import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardBody, Form, FormGroup, Label, Input, Button, Table, Alert, Spinner } from 'reactstrap';

const PaymentsContent = () => {
  console.log('[PaymentsContent] Component rendering');
  
  const [upiId, setUpiId] = useState('');
  const [savedUpiId, setSavedUpiId] = useState('');
  const [upiLoading, setUpiLoading] = useState(false);
  const [upiSaving, setUpiSaving] = useState(false);
  const [upiError, setUpiError] = useState(null);
  const [upiSuccess, setUpiSuccess] = useState(null);
  
  const [payments, setPayments] = useState([]);
  const [paymentStats, setPaymentStats] = useState({ totalEarnings: 0, paymentCount: 0 });
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [paymentsError, setPaymentsError] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  // Fetch data on mount
  useEffect(() => {
    console.log('[PaymentsContent] useEffect - fetching data');
    const fetchData = async () => {
      await Promise.all([fetchUpiId(), fetchPayments()]);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUpiId = async () => {
    setUpiLoading(true);
    setUpiError(null);
    try {
      const token = localStorage.getItem('accessToken');
      console.log('[PaymentsContent] Fetching UPI, token exists:', !!token);
      
      if (!token) {
        console.error('[PaymentsContent] No access token found');
        setUpiLoading(false);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/ambassador/upi`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('[PaymentsContent] UPI response:', response.data);
      
      if (response.data.upiId) {
        setSavedUpiId(response.data.upiId);
        setUpiId(response.data.upiId);
      }
    } catch (error) {
      console.error('[PaymentsContent] Error fetching UPI ID:', error);
      // Don't show error for first-time users (404 expected)
      if (error.response?.status !== 404) {
        setUpiError('Failed to load UPI ID');
      }
    } finally {
      setUpiLoading(false);
    }
  };

  const fetchPayments = async () => {
    setPaymentsLoading(true);
    setPaymentsError(null);
    try {
      const token = localStorage.getItem('accessToken');
      console.log('[PaymentsContent] Fetching payments, token exists:', !!token);
      
      if (!token) {
        console.error('[PaymentsContent] No access token found');
        setPaymentsLoading(false);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/ambassador/payments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('[PaymentsContent] Payments response:', response.data);
      
      setPayments(response.data.payments || []);
      setPaymentStats({
        totalEarnings: response.data.totalEarnings || 0,
        paymentCount: response.data.paymentCount || 0
      });
    } catch (error) {
      console.error('[PaymentsContent] Error fetching payments:', error);
      setPaymentsError('Failed to load payment history');
      // Set empty defaults so page still renders
      setPayments([]);
      setPaymentStats({ totalEarnings: 0, paymentCount: 0 });
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handleSaveUpiId = async (e) => {
    e.preventDefault();
    setUpiError(null);
    setUpiSuccess(null);

    // Basic validation
    if (!upiId || !upiId.trim()) {
      setUpiError('Please enter a valid UPI ID');
      return;
    }

    // UPI ID format validation
    const upiRegex = /^[\w.-]+@[\w.-]+$/;
    if (!upiRegex.test(upiId.trim())) {
      setUpiError('Invalid UPI ID format. Example: 9876543210@paytm or email@upi');
      return;
    }

    setUpiSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_BASE_URL}/ambassador/upi`,
        { upiId: upiId.trim() },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setSavedUpiId(response.data.upiId);
      setUpiSuccess('UPI ID saved successfully!');
      setTimeout(() => setUpiSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving UPI ID:', error);
      setUpiError(error.response?.data?.error || 'Failed to save UPI ID');
    } finally {
      setUpiSaving(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    } catch (e) {
      return `₹${amount}`;
    }
  };

  console.log('[PaymentsContent] Rendering with state:', {
    paymentsLoading,
    paymentsCount: payments.length,
    upiLoading,
    hasUpi: !!savedUpiId
  });

  return (
    <div>
      <div className='d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between mb-4 gap-3'>
        <div>
          <h1 className="mb-2 fw-bold" style={{ fontSize: '1.875rem', color: '#1e293b' }}>Payments</h1>
          <p className="text-muted mb-0 small">Manage your UPI details and view payment history</p>
        </div>
      </div>

      {/* Payment Stats Cards */}
      <div className="row mb-4">
        <div className="col-md-6 mb-3 mb-md-0">
          <Card className="shadow-sm border-0">
            <CardBody>
              <h6 className="text-muted mb-2">Total Earnings</h6>
              <h2 className="mb-0 text-success fw-bold">
                {formatCurrency(paymentStats.totalEarnings)}
              </h2>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-6">
          <Card className="shadow-sm border-0">
            <CardBody>
              <h6 className="text-muted mb-2">Total Payments Received</h6>
              <h2 className="mb-0 text-primary fw-bold">
                {paymentStats.paymentCount}
              </h2>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* UPI ID Section */}
      <Card className="mb-4 shadow-sm border-0">
        <CardBody>
          <h5 className="mb-2 fw-semibold">UPI Details</h5>
          <p className="text-muted small mb-3">
            Add your UPI ID to receive payments from the admin team
          </p>
          
          {upiLoading ? (
            <div className="text-center py-3">
              <Spinner color="primary" />
              <p className="text-muted small mt-2">Loading UPI details...</p>
            </div>
          ) : (
            <Form onSubmit={handleSaveUpiId}>
              <FormGroup>
                <Label for="upiId">
                  UPI ID <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="upiId"
                  placeholder="e.g., 9876543210@paytm or yourname@upi"
                  value={upiId}
                  onChange={(e) => setUpiId(e.target.value)}
                  disabled={upiSaving}
                  className="mb-2"
                />
                <small className="text-muted d-block">
                  Enter your UPI ID to receive payments. Format: phone@provider or email@upi
                </small>
              </FormGroup>

              {upiError && <Alert color="danger" className="mb-3">{upiError}</Alert>}
              {upiSuccess && <Alert color="success" className="mb-3">{upiSuccess}</Alert>}

              <Button 
                color="primary" 
                type="submit" 
                disabled={upiSaving || !upiId || upiId === savedUpiId}
              >
                {upiSaving ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  savedUpiId ? 'Update UPI ID' : 'Save UPI ID'
                )}
              </Button>
              
              {savedUpiId && (
                <small className="text-success d-block mt-2">
                  ✓ UPI ID saved: <strong>{savedUpiId}</strong>
                </small>
              )}
            </Form>
          )}
        </CardBody>
      </Card>

      {/* Payment History Section */}
      <Card className="shadow-sm border-0">
        <CardBody>
          <h5 className="mb-2 fw-semibold">Payment History</h5>
          <p className="text-muted small mb-3">
            View all payments made to your UPI ID
          </p>
          
          {paymentsError && <Alert color="danger">{paymentsError}</Alert>}
          
          <div className="table-responsive">
            <Table className="table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '8%' }}>#</th>
                  <th style={{ width: '20%' }}>Payment ID</th>
                  <th style={{ width: '15%' }}>Amount</th>
                  <th style={{ width: '20%' }}>Paid to UPI</th>
                  <th style={{ width: '15%' }}>Date</th>
                  <th style={{ width: '22%' }}>Screenshot</th>
                </tr>
              </thead>
              <tbody>
                {paymentsLoading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted mb-0 small">Loading payment history...</p>
                    </td>
                  </tr>
                ) : payments.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <div className="text-muted">
                        <div style={{ fontSize: '3rem', opacity: 0.5 }}>📭</div>
                        <p className="mt-2 mb-1 fw-semibold">No payments received yet</p>
                        <small>Payments made by admin will appear here</small>
                      </div>
                    </td>
                  </tr>
                ) : (
                  payments.map((payment, index) => (
                    <tr key={payment.id}>
                      <td>{index + 1}</td>
                      <td>
                        {payment.paymentId ? (
                          <code className="small bg-light px-2 py-1 rounded">{payment.paymentId}</code>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        <strong className="text-success">
                          {formatCurrency(payment.amount)}
                        </strong>
                      </td>
                      <td>
                        {savedUpiId ? (
                          <code className="small text-primary">{savedUpiId}</code>
                        ) : (
                          <span className="text-muted small">Not set</span>
                        )}
                      </td>
                      <td>
                        <span className="small">{formatDate(payment.paidAt)}</span>
                      </td>
                      <td>
                        {payment.screenshotUrl ? (
                          <a 
                            href={payment.screenshotUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary"
                          >
                            📷 View
                          </a>
                        ) : (
                          <span className="text-muted small">No screenshot</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default PaymentsContent;
